import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  HStack,
  Input,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { AddIcon, DownloadIcon } from '@chakra-ui/icons';
import axiosInstance from '../../../api/AxiosInstance';
import FormularioApoyo from './FormularioApoyo';

const TablaApoyos: React.FC = () => {
  const toast = useToast();
  const { isOpen: isOpenVer, onOpen: onOpenVer, onClose: onCloseVer } = useDisclosure();
  const { isOpen: isOpenNuevo, onOpen: onOpenNuevo, onClose: onCloseNuevo } = useDisclosure();

  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [data, setData] = useState<any[]>([]);
  const [cargando, setCargando] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [filtroEstatus, setFiltroEstatus] = useState('');

  const fetchData = async () => {
    setCargando(true);
    try {
      const res = await axiosInstance.get('/turismo/apoyos');
      setData(res.data || []);
    } catch {
      setData([]);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const datosFiltrados = data.filter((item) => {
    const matchEstatus = filtroEstatus ? item.estatus === filtroEstatus : true;
    const matchBusqueda = busqueda
      ? item.tipo?.toLowerCase().includes(busqueda.toLowerCase()) ||
        item.descripcion?.toLowerCase().includes(busqueda.toLowerCase())
      : true;
    return matchEstatus && matchBusqueda;
  });

  const handleVer = (item: any) => { setSelectedItem(item); onOpenVer(); };
  const handleGuardado = () => { onCloseNuevo(); fetchData(); };

  const handleAutorizar = async (item: any) => {
    try {
      await axiosInstance.put(`/turismo/apoyos/${item.id}/autorizar`);
      toast({ title: 'Apoyo autorizado', status: 'success', duration: 3000 });
      fetchData();
    } catch {
      toast({ title: 'Error al autorizar', status: 'error', duration: 3000 });
    }
  };

  const handleRechazar = async (item: any) => {
    try {
      await axiosInstance.put(`/turismo/apoyos/${item.id}/rechazar`);
      toast({ title: 'Apoyo rechazado', status: 'success', duration: 3000 });
      fetchData();
    } catch {
      toast({ title: 'Error al rechazar', status: 'error', duration: 3000 });
    }
  };

  const handleExport = (format: 'pdf' | 'excel') => {
    const base = axiosInstance.defaults.baseURL || '';
    window.open(`${base}/turismo/apoyos/export?format=${format}`, '_blank');
  };

  return (
    <Box>
      <Flex mb={4} justify="space-between" align="center" flexWrap="wrap" gap={3}>
        <HStack spacing={3} flexWrap="wrap">
          <Input
            placeholder="Buscar por tipo o descripcion..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            size="sm"
            w={{ base: 'full', md: '260px' }}
          />
          <Select placeholder="Estatus" value={filtroEstatus} onChange={(e) => setFiltroEstatus(e.target.value)} size="sm" w="160px">
            <option value="pendiente">Pendiente</option>
            <option value="autorizado">Autorizado</option>
            <option value="rechazado">Rechazado</option>
          </Select>
        </HStack>
        <HStack spacing={2}>
          <Button size="sm" leftIcon={<DownloadIcon />} colorScheme="green" variant="outline" onClick={() => handleExport('excel')}>
            Excel
          </Button>
          <Button size="sm" leftIcon={<DownloadIcon />} colorScheme="red" variant="outline" onClick={() => handleExport('pdf')}>
            PDF
          </Button>
          <Button size="sm" leftIcon={<AddIcon />} bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={onOpenNuevo}>
            Nuevo apoyo
          </Button>
        </HStack>
      </Flex>

      <Box overflowX="auto" borderRadius="md" boxShadow="md" bg="white">
        <Table variant="simple" size="sm">
          <Thead bg="#5b182b">
            <Tr>
              <Th color="white" fontSize="xs">Tipo</Th>
              <Th color="white" fontSize="xs">Monto</Th>
              <Th color="white" fontSize="xs">Fecha solicitud</Th>
              <Th color="white" fontSize="xs">Estatus</Th>
              <Th color="white" fontSize="xs">Acciones</Th>
            </Tr>
          </Thead>
          <Tbody>
            {cargando ? (
              <Tr><Td colSpan={5} textAlign="center" py={8}><Text color="gray.500">Cargando...</Text></Td></Tr>
            ) : datosFiltrados.length === 0 ? (
              <Tr><Td colSpan={5} textAlign="center" py={8}><Text color="gray.400">Sin registros</Text></Td></Tr>
            ) : (
              datosFiltrados.map((item, idx) => (
                <Tr key={item.id} bg={idx % 2 === 0 ? 'white' : 'gray.50'} _hover={{ bg: '#f7efe8' }}>
                  <Td>{item.tipo}</Td>
                  <Td>{item.monto ? `$${Number(item.monto).toLocaleString('es-MX')}` : '-'}</Td>
                  <Td>{item.fechaSolicitud ? new Date(item.fechaSolicitud).toLocaleDateString('es-MX') : '-'}</Td>
                  <Td>
                    <Badge colorScheme={item.estatus === 'autorizado' ? 'green' : item.estatus === 'rechazado' ? 'red' : 'yellow'}>
                      {item.estatus}
                    </Badge>
                  </Td>
                  <Td>
                    <HStack spacing={1} flexWrap="wrap">
                      <Button size="xs" colorScheme="blue" variant="outline" onClick={() => handleVer(item)}>Ver</Button>
                      {item.estatus === 'pendiente' && (
                        <>
                          <Button size="xs" colorScheme="green" variant="solid" onClick={() => handleAutorizar(item)}>Autorizar</Button>
                          <Button size="xs" colorScheme="red" variant="outline" onClick={() => handleRechazar(item)}>Rechazar</Button>
                        </>
                      )}
                    </HStack>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>

      <Modal isOpen={isOpenVer} onClose={onCloseVer} size="6xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent borderRadius="xl" overflow="hidden">
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b" bg="gray.50">Detalle del apoyo</ModalHeader>
          <ModalBody py={5} bg="white">
            {selectedItem && <FormularioApoyo modo="view" data={selectedItem} />}
          </ModalBody>
          <ModalFooter borderTop="1px solid" borderColor="gray.100" bg="gray.50">
            <Button onClick={onCloseVer}>Cerrar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isOpenNuevo} onClose={onCloseNuevo} size="6xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent borderRadius="xl" overflow="hidden">
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b" bg="gray.50">Nuevo apoyo</ModalHeader>
          <ModalBody py={5} bg="white">
            <FormularioApoyo modo="new" onGuardado={handleGuardado} onCancelar={onCloseNuevo} />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default TablaApoyos;


